import { useEffect, useState } from 'react';

import { Button, Box as Flex, TextField } from '@material-ui/core';

import type { JSONContent } from '@tiptap/core';

import { useCurrentEditor } from '../Context';
import { FloatingPopover } from '../FloatingPopover';

type LinkDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  anchorElement: HTMLElement | null;
  initialHref?: string;
  initialLinkText?: string;
  mode: 'create' | 'edit';
};

export const LinkDialog = ({
  isOpen,
  onClose,
  anchorElement,
  initialHref = '',
  initialLinkText = '',
  mode,
}: LinkDialogProps) => {
  const { editor } = useCurrentEditor();
  const [href, setHref] = useState('');
  const [linkText, setLinkText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setHref(initialHref);
      setLinkText(initialLinkText);
    }
  }, [isOpen, initialHref, initialLinkText]);

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editor || !href) return;

    if (mode === 'edit') {
      const { from, to } = editor.state.selection;
      const currentText = editor.state.doc.textBetween(from, to, ' ');

      if (linkText && linkText !== currentText) {
        // Preserve existing marks except for link (bold, italic, etc.)
        const existingMarks: NonNullable<JSONContent['marks']> = [];
        editor.state.doc.nodesBetween(from, to, node => {
          if (node.isText && node.marks.length > 0) {
            node.marks.forEach(mark => {
              if (mark.type.name !== 'link') {
                const markExists = existingMarks.some(
                  m => m.type === mark.type.name,
                );
                if (!markExists) {
                  existingMarks.push({ type: mark.type.name });
                }
              }
            });
          }
        });

        editor
          .chain()
          .focus()
          .deleteSelection()
          .insertContent({
            type: 'text',
            marks: [
              ...existingMarks,
              {
                type: 'link',
                attrs: { href, target: '_blank' },
              },
            ],
            text: linkText,
          })
          .run();
      } else {
        editor.chain().focus().setLink({ href, target: '_blank' }).run();
      }
    } else {
      editor.chain().focus().setLink({ href, target: '_blank' }).run();
    }

    onClose();
  };

  const handleRemove = () => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  return (
    <FloatingPopover
      isOpen={isOpen}
      onClose={onClose}
      anchorElement={anchorElement}
    >
      <form onSubmit={handleSave}>
        <Flex
          display="flex"
          flexDirection="column"
          style={{
            gap: 'var(--bui-space-2)',
          }}
        >
          <TextField
            label="URL"
            value={href}
            onChange={e => setHref(e.target.value)}
            placeholder="https://example.com"
            variant="outlined"
            size="small"
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          />
          {mode === 'edit' && (
            <TextField
              label="Link title"
              value={linkText}
              onChange={e => setLinkText(e.target.value)}
              placeholder="Enter text to display"
              variant="outlined"
              size="small"
            />
          )}
          <Flex
            display="flex"
            justifyContent="flex-end"
            style={{
              gap: 'var(--bui-space-1)',
            }}
          >
            <Button variant="outlined" size="small" type="submit">
              Save
            </Button>
            {mode === 'edit' && (
              <Button size="small" onClick={handleRemove}>
                Remove
              </Button>
            )}
          </Flex>
        </Flex>
      </form>
    </FloatingPopover>
  );
};
